import { useEffect, type ReactNode } from 'react';

import {
	Folders,
	type Icon,
	LockKey,
	Password,
	Plugs,
	ShieldCheck,
	UserSound,
} from '@phosphor-icons/react';
import LinkingFlowOpinion from './LinkingFlowOpinion';
import LinkingMethodOption from './LinkingMethodOption';
import LastPendingOrder from '../../../pages/pendingReferences/components/LastPendingOrder';
import useHeruDays from '../../../hooks/useHeruDays';
import LinkingHelpVideo, { LinkingVideoCover } from './LinkingHelpVideo';
import { trackGTMEvent } from '../../../shared/analytics/gtm';
import { EVENT_TYPES } from '../../../shared/constants/events';
import { useLinkingTracking } from './orchestration/useLinkingTracking';
import type { LinkingOrigin } from '../linkingOrigin';

/** What reconnecting is for, by the screen that asked for it. */
const RELINK_PURPOSE: Partial<Record<LinkingOrigin, string>> = {
	platforms_widget: 'actualizar tus ingresos',
};

type ConnectionOption = {
	icon: Icon;
	title: string;
	description: string;
	type: 'link' | 'efirma';
};

const connectionOptions: ConnectionOption[] = [
	{
		icon: Folders,
		title: 'Conectar con e.firma',
		description: 'Necesitas un archivo .cer y uno .key',
		type: 'efirma',
	},
	{
		icon: Password,
		title: 'Conectar con RFC y contraseña',
		description: 'Los que usas para ingresar al SAT',
		type: 'link',
	},
];

export const LinkingSecurityPoints = () => {
	return [
		{
			icon: ShieldCheck,
			iconClassName: 'text-h-primary',
			description: (
				<>
					Tu información está <b>totalmente encriptada.</b>
				</>
			),
		},
		{
			icon: UserSound,
			iconClassName: 'text-h-warning-50',
			description: (
				<>
					<b>No realizaremos ningún movimiento</b> en tu cuenta sin tu
					consentimiento.
				</>
			),
		},
		{
			icon: Plugs,
			iconClassName: 'text-h-success-50',
			description: (
				<>
					Desconecta tu cuenta del SAT en <b>cualquier momento.</b>
				</>
			),
		},
	].map((point, index) => (
		<div
			key={index}
			className='flex items-center gap-3'
		>
			<point.icon
				weight='fill'
				className={`${point.iconClassName} min-w-[22px] min-h-[22px]`}
			/>
			<p className='leading-[16px]'>{point.description}</p>
		</div>
	));
};

const LinkingFlowStep1 = ({
	handleClickOption,
	isOnboarding = false,
	efirmaOnly = false,
	relink = false,
	origin,
	rewardBanner,
}: {
	handleClickOption: (option: 'link' | 'efirma') => void;
	isOnboarding?: boolean;
	efirmaOnly?: boolean;
	relink?: boolean;
	origin?: LinkingOrigin;
	/** La promesa del experimento. Se pinta como primer hijo de la tarjeta para
	 * que la franja quede a sangre; ya viene con su bleed desde LinkingFlow. */
	rewardBanner?: ReactNode;
}) => {
	const options = efirmaOnly
		? connectionOptions.filter((o) => o.type === 'efirma')
		: connectionOptions;
	const { showHotHeru } = useHeruDays('monthly_declarations');
	const { trackMethodSelected } = useLinkingTracking();
	useEffect(() => {
		trackGTMEvent(EVENT_TYPES.GTM_LINKING_STEP_1_VIEWED);
	}, []);

	const handleOptionClick = (option: 'link' | 'efirma') => {
		trackGTMEvent(EVENT_TYPES.GTM_LINKING_STEP_1_OPTION_CLICKED, {
			type: option,
		});
		trackMethodSelected(option);
		handleClickOption(option);
	};

	// UNA sola portada para todos (decisión de producto, Rafael 2026-08-20). Antes
	// esto ramificaba en tres —video para onboarding, "declaraciones bloqueadas"
	// para quien tuviera carrito, carrusel para el resto—, pero esas ramas no eran
	// variantes comparables: cada quien veía la suya por su estado, así que la
	// diferencia de conversión entre ellas medía audiencia, no diseño.
	const getTitle = () => {
		if (relink) {
			return (
				<h2 className='font-medium text-lg md:text-xl text-center leading-tight'>
					Vuelve a conectar tu cuenta del SAT <br /> para{' '}
					<span className='bg-gradient-to-r from-h-primary-10 to-h-primary-40 bg-clip-text text-transparent'>
						{(origin && RELINK_PURPOSE[origin]) ?? 'seguir con tus declaraciones'}
					</span>
				</h2>
			);
		}

		if (isOnboarding) {
			return (
				<h2 className='font-medium text-lg md:text-xl text-center leading-tight'>
					Conecta tu cuenta del SAT <br /> para verificar tu{' '}
					<span className='bg-gradient-to-r from-h-primary-10 to-h-primary-40 bg-clip-text text-transparent'>
						perfil fiscal
					</span>
				</h2>
			);
		}

		if (showHotHeru) {
			return (
				<h2 className='font-medium text-lg md:text-xl text-center leading-tight'>
					Conecta tu cuenta del SAT  <br />y obtén{' '}  
					<span className='bg-gradient-to-r from-h-primary-10 to-h-primary-40 bg-clip-text text-transparent'>
						los siguientes beneficios
					</span>
				</h2>
			);
		}

		return (
			<h2 className='font-medium text-lg md:text-xl text-center leading-tight'>
				Conecta tu cuenta del SAT <br /> y obtén{' '}
				<span className='bg-gradient-to-r from-h-primary-10 to-h-primary-40 bg-clip-text text-transparent'>
					totalmente gratis
				</span>
			</h2>
		);
	};

	return (
		<section className='px-3 py-6 max-w-[600px] mx-auto flex flex-col gap-4 fade-in-scale text-sm -mt-3 md:-mt-0'>
			<div className='bg-white rounded-xl shadow px-2 md:px-6 py-5 md:py-8 flex flex-col gap-4 md:gap-6 relative overflow-hidden'>
				{rewardBanner}

				{getTitle()}

				<LinkingVideoCover />

				<div className='mt-10 flex flex-col gap-2 z-10'>
					{options.map((option) => (
						<LinkingMethodOption
							key={option.type}
							icon={option.icon}
							title={option.title}
							description={option.description}
							recommended={option.type === 'efirma'}
							onClick={() => handleOptionClick(option.type)}
						/>
					))}
				</div>

				<div className='text-h-primary text-center font-medium gap-2 mx-auto z-10 -mb-2 -mt-1'>
					<LockKey
						weight='fill'
						size={16}
						className='inline-block'
					/>{' '}
					Tus credenciales están 100% protegidas.
				</div>

				<div className='h-[400px] -z-1 bg-gradient-to-b from-white to-h-primary-90 absolute bottom-0 left-0 right-0' />
			</div>

			{!isOnboarding && <LinkingHelpVideo />}

			<div className='bg-white rounded-xl shadow px-6 py-5 md:py-8 flex flex-col gap-6 relative overflow-hidden'>
				<LinkingSecurityPoints />
			</div>

			<LinkingFlowOpinion />

			<LastPendingOrder isSingle={false} />
		</section>
	);
};

export default LinkingFlowStep1;
